import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const STORAGE_KEY = "kfir_a11y_settings";

const MIN_SCALE = 0.9;
const MAX_SCALE = 1.4;
const STEP = 0.1;

const DEFAULTS = {
  fontScale: 1,
  highContrast: false,
  darkMode: false,
  grayscale: false,
  highlightLinks: false,
  readableFont: false,
  bigCursor: false,
  reduceMotion: false,
};

const TOGGLE_CLASS_MAP = {
  highContrast: "a11y-high-contrast",
  darkMode: "a11y-dark",
  grayscale: "a11y-grayscale",
  highlightLinks: "a11y-highlight-links",
  readableFont: "a11y-readable-font",
  bigCursor: "a11y-big-cursor",
  reduceMotion: "a11y-reduce-motion",
};

const loadSettings = () => {
  if (typeof window === "undefined") return DEFAULTS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    return { ...DEFAULTS, ...JSON.parse(raw) };
  } catch {
    return DEFAULTS;
  }
};

const round1 = (n) => Math.round(n * 10) / 10;

const AccessibilityWidget = () => {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(loadSettings);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const [container] = useState(() => {
    if (typeof document === "undefined") return null;
    let el = document.getElementById("a11y-root");
    if (!el) {
      el = document.createElement("div");
      el.id = "a11y-root";
      document.body.appendChild(el);
    }
    return el;
  });

  useEffect(() => {
    const root = document.getElementById("root");
    if (!root) return;

    if (settings.fontScale === 1) {
      root.style.zoom = "";
    } else {
      root.style.zoom = String(settings.fontScale);
    }

    Object.entries(TOGGLE_CLASS_MAP).forEach(([key, cls]) => {
      root.classList.toggle(cls, Boolean(settings[key]));
    });

    if (settings.bigCursor) {
      document.documentElement.classList.add("a11y-big-cursor");
    } else {
      document.documentElement.classList.remove("a11y-big-cursor");
    }

    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch {
      // ignore quota / privacy errors
    }
  }, [settings]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const update = useCallback((patch) => {
    setSettings((prev) => ({ ...prev, ...patch }));
  }, []);

  const toggleKey = useCallback(
    (key) => setSettings((prev) => ({ ...prev, [key]: !prev[key] })),
    []
  );

  const increaseFont = () =>
    update({ fontScale: round1(Math.min(MAX_SCALE, settings.fontScale + STEP)) });
  const decreaseFont = () =>
    update({ fontScale: round1(Math.max(MIN_SCALE, settings.fontScale - STEP)) });

  const reset = () => setSettings(DEFAULTS);

  if (!container) return null;

  const fontPercent = Math.round(settings.fontScale * 100);
  const isAtMax = settings.fontScale >= MAX_SCALE - 0.001;
  const isAtMin = settings.fontScale <= MIN_SCALE + 0.001;

  const widget = (
    <div className="a11y-widget" dir="rtl">
      <button
        ref={triggerRef}
        type="button"
        className="a11y-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "סגירת תפריט נגישות" : "פתיחת תפריט נגישות"}
        aria-expanded={open}
        aria-haspopup="dialog"
        title="נגישות"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <circle cx="12" cy="3.6" r="1.9" fill="currentColor" />
          <path
            d="M4.5 7.5c2.4.9 4.9 1.4 7.5 1.4s5.1-.5 7.5-1.4"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            fill="none"
          />
          <path
            d="M12 8.5v5m-3.6 8 3.6-7 3.6 7"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </svg>
      </button>

      {open && (
        <div
          ref={panelRef}
          className="a11y-panel"
          role="dialog"
          aria-label="הגדרות נגישות"
        >
          <div className="a11y-panel_header">
            <h3>הגדרות נגישות</h3>
            <button
              type="button"
              className="a11y-close"
              onClick={() => {
                setOpen(false);
                triggerRef.current?.focus();
              }}
              aria-label="סגירה"
            >
              ×
            </button>
          </div>

          <div className="a11y-section">
            <span className="a11y-section_title">גודל טקסט</span>
            <div className="a11y-fontsize">
              <button
                type="button"
                onClick={decreaseFont}
                disabled={isAtMin}
                aria-label="הקטנת טקסט"
              >
                −
              </button>
              <span className="a11y-fontsize_value" aria-live="polite">
                {fontPercent}%
              </span>
              <button
                type="button"
                onClick={increaseFont}
                disabled={isAtMax}
                aria-label="הגדלת טקסט"
              >
                +
              </button>
            </div>
          </div>

          <div className="a11y-options" role="group" aria-label="התאמות תצוגה">
            <ToggleButton
              active={settings.highContrast}
              onClick={() => toggleKey("highContrast")}
              label="ניגודיות גבוהה"
            />
            <ToggleButton
              active={settings.darkMode}
              onClick={() => toggleKey("darkMode")}
              label="מצב כהה"
            />
            <ToggleButton
              active={settings.grayscale}
              onClick={() => toggleKey("grayscale")}
              label="גווני אפור"
            />
            <ToggleButton
              active={settings.highlightLinks}
              onClick={() => toggleKey("highlightLinks")}
              label="הדגשת קישורים"
            />
            <ToggleButton
              active={settings.readableFont}
              onClick={() => toggleKey("readableFont")}
              label="גופן קריא"
            />
            <ToggleButton
              active={settings.bigCursor}
              onClick={() => toggleKey("bigCursor")}
              label="סמן גדול"
            />
            <ToggleButton
              active={settings.reduceMotion}
              onClick={() => toggleKey("reduceMotion")}
              label="עצירת אנימציות"
            />
          </div>

          <button type="button" className="a11y-reset" onClick={reset}>
            איפוס הגדרות
          </button>
        </div>
      )}
    </div>
  );

  return createPortal(widget, container);
};

const ToggleButton = ({ active, onClick, label }) => (
  <button
    type="button"
    className={`a11y-toggle${active ? " active" : ""}`}
    onClick={onClick}
    aria-pressed={active}
  >
    <span className="a11y-toggle_indicator" aria-hidden="true" />
    <span className="a11y-toggle_label">{label}</span>
  </button>
);

export default AccessibilityWidget;
